import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { listPublicCourses } from "lib/siteCoursesApi";
import { siteRequest } from "lib/siteCustomerApi";
import { firstAccessDone, useFirstAccessConfirm } from "components/ios/Alert";
import {
  createCheckout,
  getCommerceCatalog,
  commerceKey,
} from "lib/siteCommerceApi";
import {
  date,
  Remote,
  Pager,
  Receipt,
  useOperation,
  useRemote,
  Notice,
} from "./Shared";
export default function Courses() {
  const { enrollmentId } = useParams();
  return (
    <CourseView key={enrollmentId || "list"} enrollmentId={enrollmentId} />
  );
}
function CourseView({ enrollmentId }) {
  const [confirmDialog, confirmFirst] = useFirstAccessConfirm();
  const [pages, setPages] = useState([null]);
  // Opening every lesson marks the course "Completed" (website launch L5).
  const [opened, setOpened] = useState([]);
  const openLesson = (lessonId) => {
    setOpened((list) => [...list, lessonId]);
    siteRequest(`/customer/courses/${enrollmentId}/lessons/${lessonId}/opened`, { method: "POST", body: {} }).catch(() => {});
  };
  const state = useRemote(
    enrollmentId
      ? "/customer/courses/" + enrollmentId
      : "/customer/courses?limit=20" +
          (pages.at(-1) ? "&after=" + pages.at(-1) : ""),
  );
  return (
    <>
      <p className="ca-eyebrow">Website learning</p>
      {confirmDialog}
      <h1>{enrollmentId ? "Your course" : "Learning"}</h1>
      <Remote state={state}>
        {state.data &&
          (enrollmentId ? (
            <>
              <Link to="/customer/access/courses">← All courses</Link>
              <section className="ca-panel">
                <h2>{state.data.enrollment.title}</h2>
                <p>Access until {date(state.data.enrollment.expiresAt)}</p>
                {state.data.enrollment.session?.startAt && (
                  <p>
                    Session:{" "}
                    {new Date(
                      state.data.enrollment.session.startAt,
                    ).toLocaleString(undefined, {
                      timeZone: state.data.enrollment.session.timeZone,
                    })}{" "}
                    ({state.data.enrollment.session.timeZone})
                  </p>
                )}
                {state.data.completedAt && <p className="ca-message">Completed {date(state.data.completedAt)}</p>}
                {state.data.live?.state === "upcoming" && <p>The live webinar starts {new Date(state.data.live.startAt).toLocaleString()}. Come back then to watch it here.</p>}
                {state.data.live?.state === "ended" && <p>This live webinar has ended.</p>}
                {state.data.youtubeEmbed ? (
                  <div style={{ position: "relative", paddingTop: "56.25%" }}>
                    <iframe title={state.data.enrollment.title} src={state.data.youtubeEmbed} allow="autoplay; encrypted-media" allowFullScreen
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
                  </div>
                ) : state.data.joinUrl && (
                  <a href={state.data.joinUrl} target="_blank" rel="noreferrer">
                    Open session instructions / join
                  </a>
                )}
              </section>
              {state.data.lessons.map((lesson) => (
                <article className="ca-panel" key={lesson.id}>
                  <h2>{lesson.title}</h2>
                  {(state.data.openedLessons || []).includes(lesson.id) || opened.includes(lesson.id) ? <p className="ca-message">{lesson.body}</p>
                    : <button type="button" className="ca-primary" onClick={() => openLesson(lesson.id)}>Open lesson</button>}
                  {lesson.hasFile && (
                    <a
                      href={`/api/site/v1/customer/courses/${enrollmentId}/files/${lesson.id}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={async (e) => {
                        if (firstAccessDone(`course:${enrollmentId}`)) return;
                        e.preventDefault();
                        const href = e.currentTarget.href;
                        if (await confirmFirst(`course:${enrollmentId}`)) window.open(href, "_blank", "noreferrer");
                      }}
                    >
                      Open protected lesson file
                    </a>
                  )}
                </article>
              ))}
            </>
          ) : (
            <>
              <div className="ca-list">
                {!state.data.items.length && (
                  <p className="ca-panel">
                    Your enrolled courses will appear here.
                  </p>
                )}
                {state.data.items.map((row) => (
                  <article className="ca-panel" key={row.id}>
                    <h2>{row.title}</h2>
                    <p>
                      {row.completedAt ? "Completed" : row.status} · Access until {date(row.expiresAt)}
                    </p>
                    {row.status === "active" && (
                      <Link to={"/customer/access/courses/" + row.id}>
                        Open course
                      </Link>
                    )}
                    {row.sourceType === "purchase" && (
                      <p>
                        <Link to={"/checkout/return?orderId=" + row.sourceId}>
                          View order
                        </Link>
                      </p>
                    )}
                  </article>
                ))}
              </div>
              <Pager pages={pages} setPages={setPages} next={state.data.next} />
            </>
          ))}
      </Remote>
    </>
  );
}
export function CourseEnrollment({ profile }) {
  const { courseId } = useParams(),
    nav = useNavigate(),
    op = useOperation();
  const [state, setState] = useState({ loading: true }),
    [provider, setProvider] = useState("stripe"),
    [terms, setTerms] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null);
  // Courses need a name and email only (website launch L5).
  const [customer, setCustomer] = useState({
    name: profile.name || "",
    email: profile.email,
  });
  const attempt = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      listPublicCourses(controller.signal),
      getCommerceCatalog(controller.signal),
    ])
      .then(([courses, catalog]) => {
        if (!controller.signal.aborted) {
          setState({ course: courses.find((c) => c.id === courseId), catalog });
          setProvider(catalog.providers.stripe.available ? "stripe" : "swift");
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) setState({ error });
      });
    return () => controller.abort();
  }, [courseId]);
  const course = state.course;
  if (state.loading) return <p>Loading course offer…</p>;
  if (state.error) return <Notice error={state.error} />;
  // Training and Orientation are bought in the TMS (L5).
  if (course?.tmsOfferUrl)
    return (
      <>
        <h1>{course.title}</h1>
        <p>Training and orientation are available in the CarrierFlow TMS.</p>
        <a className="ca-primary" href={course.tmsOfferUrl} target="_blank" rel="noopener noreferrer">Continue in the TMS</a>
      </>
    );
  if (!course?.enrollmentEnabled)
    return (
      <>
        <h1>Enrollment unavailable</h1>
        <p>This course is an outline or its offer is not open.</p>
        <Link to="/webinars">Browse courses</Link>
      </>
    );
  return (
    <>
      <p className="ca-eyebrow">Course registration</p>
      <h1>{course.title}</h1>
      <section className="ca-panel">
        <p>
          {course.offer.mode === "free"
            ? "Free registration"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(course.offer.priceMinor / 100)}
        </p>
        <p>
          {course.offer.accessDays} days of access from registration or verified
          payment.
        </p>
        <p className="ca-message">{course.offer.terms}</p>
        <form
          className="ca-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy || !terms) return;
            setError(null);
            if (course.offer.mode === "free") {
              if (
                await op.run("/customer/courses/register", {
                  courseId,
                  termsAccepted: true,
                })
              )
                nav("/customer/access/courses");
              return;
            }
            setBusy(true);
            try {
              const body = {
                items: [
                  {
                    type: course.kind === "bundle" ? "bundle" : "course",
                    courseId,
                    quantity: 1,
                  },
                ],
                customer,
                provider,
                termsAccepted: true,
              };
              const fingerprint = JSON.stringify(body);
              if (attempt.current?.fingerprint !== fingerprint)
                attempt.current = { fingerprint, key: commerceKey() };
              const result = await createCheckout(body, attempt.current.key);
              if (
                result.checkoutUrl &&
                new URL(result.checkoutUrl).hostname === "checkout.stripe.com"
              )
                window.location.assign(result.checkoutUrl);
              else nav("/checkout/return?orderId=" + result.order.id);
            } catch (error) {
              setError(error);
            } finally {
              setBusy(false);
            }
          }}
        >
          {course.offer.mode === "paid" && (
            <>
              {Object.entries(customer).map(([key, value]) => (
                <label key={key}>
                  {{ name: "Full name", email: "Email" }[key] || key}
                  <input
                    required
                    type={key === "email" ? "email" : "text"}
                    value={value}
                    readOnly={key === "email"}
                    maxLength={key === "email" ? 254 : 200}
                    onChange={(e) =>
                      setCustomer({ ...customer, [key]: e.target.value })
                    }
                  />
                </label>
              ))}
              <label>
                Payment method
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  {["stripe", "swift"].map((p) => (
                    <option
                      key={p}
                      value={p}
                      disabled={!state.catalog.providers[p].available}
                    >
                      {p === "stripe" ? "Stripe" : "SWIFT bank transfer"}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label className="ca-check">
            <input
              type="checkbox"
              required
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
            />
            I accept the course offer and{" "}
            <Link to="/terms-of-use">website terms</Link>.
          </label>
          <Notice error={error} />
          <Receipt op={op} />
          <button className="ca-primary" disabled={busy || op.busy || !terms}>
            {course.offer.mode === "free"
              ? "Register for free"
              : "Continue to payment"}
          </button>
        </form>
      </section>
    </>
  );
}
